/**
 * Resource-pool bar color, keyed by the CONTENT resource id (#344) — the same
 * `resourceId` `resourceLabels.ts` auto-derives off a champion
 * (`ResourceDef.id`, e.g. "rage"/"energy"/"mana"). Unlike `resourceLabels.ts`
 * (#465: build-time content-glob derived), THIS table stays a small,
 * genuinely hand-kept map — color is a client-only design choice with no
 * content-side source to derive from (content has no notion of "what color is
 * this resource"), so there's nothing to auto-derive here. This is what lets
 * the SAME resource id color the bar identically everywhere it renders — the
 * above-head world-space bar (`render/entityView.ts`) AND the HUD frame bar
 * (`ui/FrameResourceBar.tsx`).
 *
 * WHY color-code at all: the HP bar is now ALWAYS green (#344 — health
 * fraction is already conveyed by bar WIDTH; a red/yellow/green fade on TOP
 * of that was redundant and, worse, made a low-health resource bar look like
 * a low-health HP bar at a glance). Coloring the RESOURCE bar by pool
 * identity instead gives the WoW-familiar at-a-glance read: rage is red,
 * energy is yellow, mana is blue.
 *
 * FALLBACK: an id absent from this table (a future resource type not yet
 * colored, or `resourceId === null` — an unmapped/pool-less champion) gets
 * the light-purple PLACEHOLDER (`RESOURCE_COLOR_UNKNOWN`), never silently
 * reusing another resource's color. Mirrors `championColors.ts`'s
 * numeric-Pixi-color + `*Css` DOM-string duality.
 */

import type { ResourceId } from "@pit/protocol";

/** Rage — numeric (Pixi format, like `render/colors.ts`). */
export const RESOURCE_COLOR_RAGE = 0xc0392b;
/** Energy. */
export const RESOURCE_COLOR_ENERGY = 0xf1c40f;
/** Mana. */
export const RESOURCE_COLOR_MANA = 0x2f7ed8;
/** Placeholder for an unmapped/future resource id — light purple. */
export const RESOURCE_COLOR_UNKNOWN = 0xb28dd6;

/**
 * `resourceId → color`. Keyed by the OPAQUE content id string (not a
 * champion), so any future champion sharing the same pool (a second rage
 * user, say) is colored for free — no per-champion row needed.
 */
const COLOR_BY_RESOURCE_ID: Readonly<Record<string, number>> = {
  rage: RESOURCE_COLOR_RAGE,
  energy: RESOURCE_COLOR_ENERGY,
  mana: RESOURCE_COLOR_MANA,
};

/**
 * The color for `resourceId` — its mapped color, or the light-purple
 * placeholder for `null` / an unmapped id. Always returns a usable color
 * (never null); Pixi numeric form. Pure lookup.
 */
export function resourceColor(resourceId: ResourceId | null): number {
  if (resourceId === null) return RESOURCE_COLOR_UNKNOWN;
  return COLOR_BY_RESOURCE_ID[resourceId] ?? RESOURCE_COLOR_UNKNOWN;
}

/**
 * `resourceColor` as a `#rrggbb` CSS string, for the DOM HUD frames
 * (`FrameResourceBar.tsx`) — Pixi takes the numeric form directly. Pure.
 */
export function resourceColorCss(resourceId: ResourceId | null): string {
  return `#${resourceColor(resourceId).toString(16).padStart(6, "0")}`;
}
